import React, { useEffect, useState } from "react";
import { config } from "../config/app.config";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Button, Text, Actions } from "braid-design-system";
import { getPlAccount } from "../services/plAccountService";
import { fetchGuarantorInsurance } from "../services/loanDynamicFieldsService";
import apiClient from "../clients/apiClient";
import Loading from "../components/Loading";
import { formatCurrency } from "../utils/currencyUtils";
import { useToast } from "../hooks/useToast";

const labels: { [key: string]: { [lang: string]: string } } = {
  title: {
    en: "GUARANTOR'S INSURANCE CARD",
    si: "ඇපකරුවන්ගේ රක්ෂණ කාඩ්පත",
    ta: "ஜாமீன் தாரர் காப்பீட்டு அட்டை"
  },
  institute: {
    en: "Institute",
    si: "ආයතනය",
    ta: "நிறுவனம்"
  },
  branch: {
    en: "Branch",
    si: "ශාඛාව",
    ta: "கிளை"
  },
  date: {
    en: "Date",
    si: "දිනය",
    ta: "தேதி"
  },
  loanNo: {
    en: "Loan Account Number",
    si: "ණය ගිණුම් අංකය",
    ta: "கடன் கணக்கு எண்"
  },
  productName: {
    en: "Product Name",
    si: "නිෂ්පාදන නාමය",
    ta: "தயாரிப்பு பெயர்"
  },
  capital: {
    en: "Loan Capital",
    si: "ණය මුදල",
    ta: "கடன் தொகை"
  },
  interest: {
    en: "Interest Rate",
    si: "පොලී අනුපාතය",
    ta: "வட்டி விகிதம்"
  },
  customerName: {
    en: "Customer Name",
    si: "ගනුදෙනුකරුගේ නම",
    ta: "வாடிக்கையாளர் பெயர்"
  },
  memberNo: {
    en: "Member Number",
    si: "සාමාජික අංකය",
    ta: "உறுப்பினர் எண்"
  },
  nic: {
    en: "NIC",
    si: "ජා.හැ.අංකය",
    ta: "தேசிய அடையாள அட்டை எண்"
  },
  insuranceInstalment: {
    en: "Insurance Installment",
    si: "රක්ෂණ වාරිකය",
    ta: "காப்பீட்டு தவணை"
  },
  insuranceId: {
    en: "Insurance ID",
    si: "රක්ෂණ අංකය",
    ta: "காப்பீட்டு ஐடி"
  },
  guarantorsHeader: {
    en: "Guarantors Name & Insurance Percentage",
    si: "ඇපකරුවන්ගේ නම සහ රක්ෂණ ප්‍රතිශතය",
    ta: "ஜாமீன் தாரர் பெயர் & காப்பீட்டு சதவீதம்"
  },
  guarantorName: {
    en: "Guarantor Name",
    si: "ඇපකරුගේ නම",
    ta: "ஜாமீன் தாரர் பெயர்"
  },
  percentage: {
    en: "Percentage",
    si: "ප්‍රතිශතය",
    ta: "சதவீதம்"
  },
  customerCopy: {
    en: "Customer Copy",
    si: "ගනුදෙනුකරුගේ පිටපත",
    ta: "வாடிக்கையாளர் நகல்"
  },
  companyCopy: {
    en: "Company Copy",
    si: "ආයතනයේ පිටපත",
    ta: "நிறுவனத்தின் நகல்"
  },
  managerSignature: {
    en: "Manager's Signature",
    si: "කළමනාකරුගේ අත්සන",
    ta: "மேலாளர் கையொப்பம்"
  },
  customerSignature: {
    en: "Customer's Signature",
    si: "ගනුදෙනුකරුගේ අත්සන",
    ta: "வாடிக்கையாளர் கையொப்பம்"
  }
};

const GuarantorInsurancePrint: React.FC = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const accountId = Number(queryParams.get("accountId"));

  const { i18n } = useTranslation();
  const toast = useToast();

  const [loading, setLoading] = useState(true);
  const [account, setAccount] = useState<any>(null);
  const [customer, setCustomer] = useState<any>(null);
  const [insuranceData, setInsuranceData] = useState<any>(null);
  const [logoUrl, setLogoUrl] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (account) {
      const instLogo = account.institute_logo || account.branch?.institute?.logo;
      if (instLogo) {
        if (instLogo.startsWith('data:') || instLogo.startsWith('http')) {
          setLogoUrl(instLogo);
        } else {
          const cleanedPath = instLogo.startsWith('/') ? instLogo : `/${instLogo}`;
          let urlPath = cleanedPath;
          if (urlPath.startsWith('/uploads/')) {
            urlPath = urlPath.replace('/uploads/', '/api/uploads/');
          }
          const backendHost = config.apiUrl.replace(/\/api\/?$/, '');
          setLogoUrl(`${backendHost}/api/uploads/${instLogo.replace('uploads/', '')}`);
        }
      } else {
        setLogoUrl(undefined);
      }
    }
  }, [account]);

  const getNormalizedLang = () => {
    const l = (i18n.language || "en").toLowerCase();
    if (l.startsWith("si")) return "si";
    if (l.startsWith("ta")) return "ta";
    return "en";
  };
  const normLang = getNormalizedLang();

  const getLabel = (key: string) => {
    return labels[key]?.[normLang] || labels[key]?.["en"] || "";
  };

  const getCustomerById = async (customerId: number) => {
    try {
      const response = await apiClient.get(`/customer/id/${customerId}`);
      return response.data.data || null;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!accountId) return;
      setLoading(true);
      try {
        const [accountData, insRes] = await Promise.all([
          getPlAccount(accountId),
          fetchGuarantorInsurance(accountId).catch(() => null)
        ]);

        setAccount(accountData);
        setInsuranceData(insRes);

        if (accountData?.ci_customer_id) {
          const customerData = await getCustomerById(accountData.ci_customer_id);
          setCustomer(customerData);
        }
      } catch (error: any) {
        toast.showError(`Error loading insurance print data: ${error.message}`);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [accountId]);

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return <Loading message="Loading Guarantor Insurance data..." />;
  }

  if (!account || !insuranceData) {
    return (
      <Box padding="large">
        <Text tone="critical" weight="strong">No Guarantor Insurance records found for this account.</Text>
      </Box>
    );
  }

  // Language selectors
  const instName = account.branch?.institute?.[normLang === "si" ? "name_ln2" : normLang === "ta" ? "name_ln3" : "name_ln1"] || account.branch?.institute?.name_ln1 || account[normLang === "si" ? "institute_name_ln2" : normLang === "ta" ? "institute_name_ln3" : "institute_name_ln1"] || account.institute_name || "";
  const branchName = account.branch?.[normLang === "si" ? "name_ln2" : normLang === "ta" ? "name_ln3" : "name_ln1"] || account.branch?.name_ln1 || "";
  const prodName = account.account_type?.[normLang === "si" ? "name_ln2" : normLang === "ta" ? "name_ln3" : "name_ln1"] || account.account_type?.name_ln1 || "";
  const custName = customer?.[normLang === "si" ? "full_name_ln2" : normLang === "ta" ? "full_name_ln3" : "full_name_ln1"] || customer?.full_name_ln1 || "";
  const currentDate = new Date().toLocaleDateString();

  const renderCardContent = (copyType: "customer" | "company") => {
    return (
      <div style={{
        padding: "16px",
        border: "2px solid #333",
        borderRadius: "8px",
        height: "100%",
        boxSizing: "border-box",
        backgroundColor: "#fff",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between"
      }}>
        {/* Header */}
        <div>
          <div style={{ display: "flex", alignItems: "center", marginBottom: "12px", borderBottom: "1px solid #ccc", paddingBottom: "8px" }}>
            {logoUrl && <img src={logoUrl} alt="Logo" style={{ height: "40px", width: "auto", marginRight: "12px" }} />}
            <div>
              <div style={{ fontSize: "16px", fontWeight: "bold", textTransform: "uppercase" }}>{instName}</div>
              <div style={{ fontSize: "13px", color: "#666" }}>
                {getLabel("branch")}: {branchName} | {getLabel("date")}: {currentDate}
              </div>
            </div>
          </div>

          <div style={{ textAlign: "center", fontSize: "15px", fontWeight: "bold", margin: "8px 0", color: "#222", borderBottom: "1.5px solid #222", paddingBottom: "4px" }}>
            {getLabel("title")}
          </div>

          {/* Body */}
          <table style={{ width: "100%", fontSize: "13px", borderCollapse: "collapse", marginBottom: "16px" }}>
            <tbody>
              <tr>
                <td style={{ padding: "4px 0", fontWeight: "bold", width: "45%" }}>{getLabel("loanNo")}:</td>
                <td style={{ padding: "4px 0" }}>{account.ref_account_number || account.account_number}</td>
              </tr>
              <tr>
                <td style={{ padding: "4px 0", fontWeight: "bold" }}>{getLabel("productName")}:</td>
                <td style={{ padding: "4px 0" }}>{prodName}</td>
              </tr>
              <tr>
                <td style={{ padding: "4px 0", fontWeight: "bold" }}>{getLabel("capital")}:</td>
                <td style={{ padding: "4px 0" }}>{account.capital != null ? formatCurrency(account.capital) : ""}</td>
              </tr>
              <tr>
                <td style={{ padding: "4px 0", fontWeight: "bold" }}>{getLabel("interest")}:</td>
                <td style={{ padding: "4px 0" }}>{account.interest_rate ? `${account.interest_rate}%` : ""}</td>
              </tr>
              <tr>
                <td style={{ padding: "4px 0", fontWeight: "bold" }}>{getLabel("insuranceInstalment")}:</td>
                <td style={{ padding: "4px 0" }}>{formatCurrency(insuranceData.insurance_instalment || 0)}</td>
              </tr>
              <tr>
                <td style={{ padding: "4px 0", fontWeight: "bold" }}>{getLabel("customerName")}:</td>
                <td style={{ padding: "4px 0" }}>{custName}</td>
              </tr>
              <tr>
                <td style={{ padding: "4px 0", fontWeight: "bold" }}>{getLabel("memberNo")}:</td>
                <td style={{ padding: "4px 0" }}>{customer?.customer_number || ""}</td>
              </tr>
              <tr>
                <td style={{ padding: "4px 0", fontWeight: "bold" }}>{getLabel("nic")}:</td>
                <td style={{ padding: "4px 0" }}>{customer?.nic || ""}</td>
              </tr>
            </tbody>
          </table>

          {/* Bottom - Guarantors */}
          <div style={{ fontSize: "13px", fontWeight: "bold", borderBottom: "1px solid #333", paddingBottom: "4px", marginBottom: "6px" }}>
            {getLabel("guarantorsHeader")}
          </div>
          <table style={{ width: "100%", fontSize: "13px", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: "1px solid #eee" }}>
                <th style={{ textAlign: "left", padding: "4px 0" }}>{getLabel("guarantorName")}</th>
                <th style={{ textAlign: "right", padding: "4px 0" }}>{getLabel("percentage")} (%)</th>
              </tr>
            </thead>
            <tbody>
              {insuranceData.insurance_info?.map((item: any, i: number) => {
                const gName = item[normLang === "si" ? "name_si" : normLang === "ta" ? "name_ta" : "name_en"] || item.name_en || "";
                return (
                  <tr key={i} style={{ borderBottom: "1px solid #fafafa" }}>
                    <td style={{ padding: "4px 0" }}>{gName}</td>
                    <td style={{ padding: "4px 0", textAlign: "right" }}>{item.percentage}%</td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Signatures */}
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: "30px", marginBottom: "8px", fontSize: "12px" }}>
            <div style={{ textAlign: "center", width: "45%" }}>
              <div style={{ borderBottom: "1px solid #333", height: "30px", marginBottom: "4px" }}></div>
              <div>{getLabel("managerSignature")}</div>
            </div>
            <div style={{ textAlign: "center", width: "45%" }}>
              <div style={{ borderBottom: "1px solid #333", height: "30px", marginBottom: "4px" }}></div>
              <div>{getLabel("customerSignature")}</div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: "1px dashed #ccc", paddingTop: "8px", marginTop: "12px", fontSize: "12px", fontWeight: "bold" }}>
          <div>
            {getLabel("insuranceId")}: {insuranceData.id}
          </div>
          <div style={{ textTransform: "uppercase" }}>
            [{getLabel(copyType === "customer" ? "customerCopy" : "companyCopy")}]
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <Box padding="medium">
        <style>{`
        @media print {
          .no-print {
            display: none !important;
          }
          body {
            background-color: #fff !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          @page {
            size: A4 landscape;
            margin: 0.5cm;
          }
          #print-page {
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            width: 100% !important;
            max-width: 100% !important;
            height: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
            box-shadow: none !important;
            border: none !important;
            background-color: #fff !important;
            display: flex !important;
            flex-direction: column !important;
            box-sizing: border-box !important;
          }
          .print-flex-container {
            height: 100% !important;
            flex: 1 !important;
          }
        }
      `}</style>

        {/* Main Print Container */}
        <div id="print-page" className="print-area" style={{
          width: "100%",
          maxWidth: "1100px",
          margin: "0 auto",
          backgroundColor: "#fff",
          boxShadow: "0 0 10px rgba(0,0,0,0.05)",
          border: "1px solid #eaeaea",
          borderRadius: "8px",
          padding: "20px",
          boxSizing: "border-box"
        }}>
          <div className="print-flex-container" style={{ display: "flex", flexDirection: "row", width: "100%" }}>
            {/* Customer Copy */}
            <div style={{ flex: 1, paddingRight: "16px", display: "flex", flexDirection: "column" }}>
              {renderCardContent("customer")}
            </div>

            {/* Divider */}
            <div style={{
              borderLeft: "2px dashed #999",
              margin: "0 8px",
              height: "auto"
            }}></div>

            {/* Company Copy */}
            <div style={{ flex: 1, paddingLeft: "16px", display: "flex", flexDirection: "column" }}>
              {renderCardContent("company")}
            </div>
          </div>
        </div>
      </Box>
      {/* Screen Print Button */}
      <Box className="no-print" marginBottom="medium" display="flex" justifyContent="center">
        <Actions>
          <Button variant="ghost" onClick={handlePrint} tone="brandAccent">Print Guarantor Insurance Card</Button>
        </Actions>
      </Box>
    </>

  );
};

export default GuarantorInsurancePrint;
